import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/helpers/cn";
import { Info2Icon, Star3Icon } from "@/shared/icons";
import { Tooltip } from "@/shared/ui";
import FactionBlockLayout from "@/domains/faction/components/FactionBlockLayout";
import FactionProgressLine from "@/domains/faction/components/FactionProgressLine";
import FactionTooltipContent from "@/domains/faction/components/FactionTooltipContent";

type Props = {
  currentCount: number;
  maxCount: number;
  className?: string;
  color?: string;
  isSoon?: boolean;
};

export default function FactionProgressInfo({
  currentCount,
  maxCount,
  className,
  color,
  isSoon,
}: Props) {
  const { t } = useTranslation("faction");

  const progress = useMemo(
    () => Math.max(0, Math.min(100, (currentCount / Math.max(1, maxCount)) * 100)),
    [currentCount, maxCount],
  );

  const tooltipContent = (
    <FactionTooltipContent
      title={t("faction.tooltip.1.title")}
      description={t("faction.tooltip.1.description")}
      className="rounded-bl-none"
      isDecor
    />
  );

  return (
    <div
      className={cn("relative p-4 rounded-xl", className)}
      style={{
        background: `radial-gradient(50% 57.35% at 50% 100%, ${color}33 0%, ${color}00 100%), #181818`,
      }}
    >
      {isSoon && <FactionBlockLayout text={`${t("faction.soon")}...`} />}
      <div className="flex items-center mb-3.5">
        <Star3Icon className="size-4.5 text-[#B8B8B8]" />
        <p className="ml-1.5 text-12 font-bold uppercase leading-none">
          {t("faction.factionProgress")}
        </p>
        <Tooltip content={tooltipContent} className="ml-auto" tooltipClassName="bottom-2 left-6">
          <Info2Icon className="size-4.5 text-white/30 transition-all duration-200 hover:text-white" />
        </Tooltip>
      </div>
      <div className="flex items-center mb-3">
        <div className="flex items-end gap-1">
          <p className="text-20 font-bold leading-none">{currentCount}</p>
          <p className="text-13 font-medium text-[#B8B8B8] leading-none">/{maxCount}</p>
        </div>
        {currentCount === maxCount && (
          <p className="ml-auto text-12 font-bold text-accent-solid leading-none uppercase">
            {t("faction.maximum")}
          </p>
        )}
      </div>
      <FactionProgressLine progress={progress} />
    </div>
  );
}
